import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { BrowserQRCodeReader } from '@zxing/browser';
import { useTranslation } from 'react-i18next';
import { normalizeContainerFilterInput } from '../utils/containerSearch';

const getCameraErrorMessage = (err, t) => {
  const name = err?.name || '';
  const message = err?.message || '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return t('operation.qr_scanner.permission_denied');
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return t('operation.qr_scanner.no_camera');
  }
  if (name === 'NotReadableError') {
    return t('operation.qr_scanner.camera_in_use');
  }
  if (name === 'SecurityError') {
    return t('operation.qr_scanner.https_required');
  }
  if (message) {
    return t('operation.qr_scanner.unable_to_access_with_message', { message });
  }
  return t('operation.qr_scanner.unable_to_access');
};

const waitForVideoElement = async (ref, attempts = 10, delayMs = 50) => {
  for (let i = 0; i < attempts; i += 1) {
    if (ref.current) return ref.current;
    // Allow the dialog/video element to mount before accessing it.
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => {
      setTimeout(resolve, delayMs);
    });
  }
  return null;
};

export default function ContainerSearchField({
  variant = 'filter',
  value,
  onChange,
  onSubmitSearch,
  onSelectOption,
  options = [],
  maxWidth,
  accentColor,
}) {
  const { t } = useTranslation();
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [scanError, setScanError] = useState('');
  const [scanErrorDetails, setScanErrorDetails] = useState('');
  const [isScannerSupported, setIsScannerSupported] = useState(true);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanRafRef = useRef(null);
  const detectorRef = useRef(null);
  const zxingControlsRef = useRef(null);
  const zxingReaderRef = useRef(null);

  useEffect(() => {
    const supported = typeof window !== 'undefined'
      && !!navigator?.mediaDevices?.getUserMedia;
    setIsScannerSupported(supported);
  }, []);

  const stopScanner = useCallback(() => {
    if (scanRafRef.current) {
      cancelAnimationFrame(scanRafRef.current);
      scanRafRef.current = null;
    }
    if (zxingControlsRef.current) {
      zxingControlsRef.current.stop();
      zxingControlsRef.current = null;
    }
    if (zxingReaderRef.current) {
      if (typeof zxingReaderRef.current.reset === 'function') {
        zxingReaderRef.current.reset();
      }
      zxingReaderRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }
    detectorRef.current = null;
  }, []);

  const submitSearch = useCallback((rawValue) => {
    const normalizedValue = normalizeContainerFilterInput(rawValue);
    onChange(normalizedValue);
    if (normalizedValue && onSubmitSearch) {
      onSubmitSearch(normalizedValue);
    }
  }, [onChange, onSubmitSearch]);

  const startScanner = useCallback(async () => {
    setScanError('');
    setScanErrorDetails('');
    if (!isScannerSupported) {
      setScanError(t('operation.qr_scanner.not_supported'));
      setScanErrorDetails('reason=unsupported');
      return;
    }
    if (typeof window !== 'undefined' && !window.isSecureContext) {
      setScanError(t('operation.qr_scanner.https_required'));
      setScanErrorDetails(`origin=${window.location?.origin || 'unknown'}`);
      return;
    }
    try {
      const videoEl = await waitForVideoElement(videoRef);
      if (!videoEl) {
        setScanError(t('operation.qr_scanner.unable_to_access'));
        setScanErrorDetails('reason=video-element-not-ready');
        return;
      }

      if (typeof window !== 'undefined' && 'BarcodeDetector' in window) {
        detectorRef.current = new window.BarcodeDetector({ formats: ['qr_code'] });
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        streamRef.current = stream;
        videoEl.srcObject = stream;
        await videoEl.play();

        const scan = async () => {
          if (!videoEl || !detectorRef.current) return;
          if (videoEl.readyState >= 2) {
            try {
              const barcodes = await detectorRef.current.detect(videoEl);
              if (barcodes.length > 0) {
                const rawValue = barcodes[0]?.rawValue || barcodes[0]?.data || '';
                if (rawValue) {
                  submitSearch(rawValue);
                  setIsScannerOpen(false);
                  return;
                }
              }
            } catch (err) {
              setScanError(t('operation.qr_scanner.unable_to_read'));
              return;
            }
          }
          scanRafRef.current = requestAnimationFrame(scan);
        };

        scanRafRef.current = requestAnimationFrame(scan);
      } else {
        const codeReader = new BrowserQRCodeReader();
        zxingReaderRef.current = codeReader;
        const constraints = {
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        };
        const controls = await codeReader.decodeFromConstraints(
          constraints,
          videoEl,
          (result) => {
            if (!result) return;
            const rawValue = result.getText ? result.getText() : `${result}`;
            if (rawValue) {
              submitSearch(rawValue);
              setIsScannerOpen(false);
            }
          },
        );
        zxingControlsRef.current = controls;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[QR] camera error', err);
      setScanError(getCameraErrorMessage(err, t));
      const name = err?.name ? `name=${err.name}` : '';
      const message = err?.message ? `message=${err.message}` : '';
      const fallback = err ? `error=${String(err)}` : '';
      const details = [name, message, fallback].filter(Boolean).join(', ');
      if (details) {
        setScanErrorDetails(details);
      }
    }
  }, [isScannerSupported, submitSearch, t]);

  useEffect(() => {
    if (isScannerOpen) {
      startScanner();
    } else {
      stopScanner();
      setScanError('');
    }
    return () => stopScanner();
  }, [isScannerOpen, startScanner, stopScanner]);

  const handleInputChange = useCallback((event) => {
    onChange(normalizeContainerFilterInput(event.target.value));
  }, [onChange]);

  const handleKeyDown = useCallback((event) => {
    if (variant === 'filter' && event.key === 'Enter' && onSubmitSearch) {
      event.preventDefault();
      submitSearch(event.currentTarget.value);
    }
  }, [onSubmitSearch, submitSearch, variant]);

  const handleAutocompleteInputChange = useCallback((event, newInputValue, reason) => {
    if (reason === 'reset') {
      onChange(newInputValue);
      return;
    }

    onChange(normalizeContainerFilterInput(newInputValue));
  }, [onChange]);

  const handleAutocompleteChange = useCallback((event, newValue) => {
    if (!newValue) return;

    if (typeof newValue === 'string') {
      submitSearch(newValue);
      return;
    }

    onChange(newValue.label);

    if (onSelectOption) {
      onSelectOption(newValue);
      return;
    }

    if (onSubmitSearch) {
      onSubmitSearch(newValue.label);
    }
  }, [onChange, onSelectOption, onSubmitSearch, submitSearch]);

  const getFilteredAutocompleteOptions = useCallback((availableOptions, inputValue) => {
    const searchText = normalizeContainerFilterInput(inputValue).toLowerCase();

    if (!searchText) {
      return availableOptions.slice(0, 20);
    }

    return availableOptions
      .filter((option) => option.searchText.includes(searchText))
      .slice(0, 20);
  }, []);

  const filterAutocompleteOptions = useCallback((availableOptions, state) => (
    getFilteredAutocompleteOptions(availableOptions, state.inputValue)
  ), [getFilteredAutocompleteOptions]);

  const filteredAutocompleteOptions = useMemo(() => (
    getFilteredAutocompleteOptions(options, value)
  ), [getFilteredAutocompleteOptions, options, value]);

  const handleAutocompleteKeyDown = useCallback((event) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    event.stopPropagation();

    const normalizedValue = normalizeContainerFilterInput(value);
    if (!normalizedValue) return;

    const exactMatches = filteredAutocompleteOptions.filter(
      (option) => normalizeContainerFilterInput(option.label).toLowerCase()
        === normalizedValue.toLowerCase(),
    );

    if (exactMatches.length === 1 && onSelectOption) {
      onChange(exactMatches[0].label);
      onSelectOption(exactMatches[0]);
      return;
    }

    if (filteredAutocompleteOptions.length === 1 && onSelectOption) {
      onChange(filteredAutocompleteOptions[0].label);
      onSelectOption(filteredAutocompleteOptions[0]);
      return;
    }

    submitSearch(normalizedValue);
  }, [filteredAutocompleteOptions, onChange, onSelectOption, submitSearch, value]);

  const textFieldSx = accentColor ? {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: `${accentColor}.main`,
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: `${accentColor}.main`,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: `${accentColor}.main`,
    },
  } : undefined;

  return (
    <>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        width: '100%',
        ...(maxWidth ? { maxWidth } : {}),
      }}
      >
        {variant === 'autocomplete' ? (
          <Autocomplete
            freeSolo
            autoHighlight
            blurOnSelect
            clearOnBlur={false}
            disableClearable
            forcePopupIcon={false}
            options={options}
            value={null}
            inputValue={value}
            onInputChange={handleAutocompleteInputChange}
            onChange={handleAutocompleteChange}
            filterOptions={filterAutocompleteOptions}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
            isOptionEqualToValue={(option, selectedValue) => option.key === selectedValue.key}
            renderOption={(props, option) => (
              <Box
                component="li"
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                key={option.key}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 0.25,
                }}
              >
                <Typography variant="body2">{option.label}</Typography>
                {option.description ? (
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                ) : null}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                label={t('operation.filter_by_name')}
                variant="outlined"
                size="small"
                onKeyDown={handleAutocompleteKeyDown}
                sx={{ flex: 1, ...textFieldSx }}
              />
            )}
            sx={{ flex: 1 }}
          />
        ) : (
          <TextField
            label={t('operation.filter_by_name')}
            variant="outlined"
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            size="small"
            sx={{ flex: 1, ...textFieldSx }}
          />
        )}
        <IconButton
          aria-label={t('operation.qr_scanner.title')}
          onClick={() => setIsScannerOpen(true)}
          disabled={!isScannerSupported}
          sx={accentColor ? {
            color: 'common.white',
            backgroundColor: `${accentColor}.main`,
            borderRadius: 1.5,
            px: 1.5,
            minWidth: 52,
            minHeight: 40,
            '&:hover': {
              backgroundColor: `${accentColor}.dark`,
            },
            '&.Mui-disabled': {
              backgroundColor: 'action.disabledBackground',
              color: 'action.disabled',
            },
          } : undefined}
        >
          <QrCodeScannerIcon fontSize={accentColor ? 'medium' : 'small'} />
        </IconButton>
      </Box>
      <Dialog
        open={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('operation.qr_scanner.title')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              width: '100%',
              borderRadius: 1,
              overflow: 'hidden',
              backgroundColor: 'black',
              aspectRatio: '3 / 4',
              position: 'relative',
            }}
          >
            <video
              ref={videoRef}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              playsInline
              muted
            />
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: '80%',
                  maxWidth: 300,
                  aspectRatio: '1 / 1',
                  borderRadius: 2,
                  border: '2px solid rgba(255,255,255,0.9)',
                  boxShadow: '0 0 0 999px rgba(0,0,0,0.25)',
                }}
              />
            </Box>
          </Box>
          {scanError ? (
            <>
              <Typography color="error" variant="body2">{scanError}</Typography>
              {scanErrorDetails ? (
                <Typography color="error" variant="caption">{scanErrorDetails}</Typography>
              ) : null}
            </>
          ) : (
            <Typography variant="body2">
              {t('operation.qr_scanner.instructions')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsScannerOpen(false)}>{t('close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
